import { isAudioFile, isVideoFile } from '@lib/utils/fileExtensions';
import { toError } from '@lib/utils/toError';
import * as Sentry from '@sentry/react';
import type { GeneralTrack, MediaInfo, MediaInfoResult, VideoTrack } from 'mediainfo.js';

/** What a media file's container states about it. A field is absent when the container doesn't say. */
export interface ProbedMedia {
  durationSeconds?: number;
  /** Display-oriented, like `<video>.videoWidth`: swapped for a stream rotated a quarter turn. */
  width?: number;
  height?: number;
}

// Covers fetching the ~1 MB WASM on first use as well as the analysis itself (3–50 ms for files up
// to 5 GB). Without it, a download that stalls would hold every probe queued behind it — and the
// upload modal's metadata batch with them — forever.
const PROBE_TIMEOUT_MS = 15_000;

// MediaInfoLib 0.3.5 divides an AU file's data size by its sample rate alone, ignoring sample width
// and channels: a 12.5 s mono 16-bit file reads 25 s, stereo 50 s. An overestimate could block an
// upload for credits it won't spend, so these stay unknown.
const MISREAD_EXTENSIONS = new Set(['au']);

let instance: Promise<MediaInfo<'object'>> | undefined;
let queue: Promise<unknown> = Promise.resolve();
type ProbeFailure = 'load' | 'timeout' | 'analyze';
const reportedFailures = new Set<ProbeFailure>();

/** Whether `probeMediaFile` can read this file: an accepted video or audio type MediaInfo gets right. */
export function canProbeMediaFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return (isVideoFile(filename) || isAudioFile(filename)) && !MISREAD_EXTENSIONS.has(ext);
}

/**
 * Reads duration and dimensions from a file the browser can't play (MKV, AVI, MPEG-TS, FLV, WMA,
 * AC-3, …) with MediaInfoLib compiled to WASM. Nothing is decoded: it reads the headers and, for
 * formats with no duration field, the end of the file. The module (~1 MB compressed) is fetched
 * on first use, so uploads of browser-playable files never download it.
 *
 * Analyses run one at a time because a MediaInfo instance rejects a second concurrent call.
 */
export function probeMediaFile(file: Blob): Promise<ProbedMedia> {
  // mediainfo.js 0.3.5 answers a 0-byte input read through an async reader with the previous
  // file's result (a WavPack's 12.5 s came back for an empty .mkv). There is nothing to read anyway.
  if (file.size === 0) return Promise.resolve({});
  let abandoned = false;
  const turn = queue.then(() => {
    // Its caller already timed out; don't spend a turn (or a WASM download) on it.
    if (abandoned) throw new Error('Probe abandoned before its turn');
    const controller = new AbortController();
    return withTimeout(analyze(file, controller.signal), PROBE_TIMEOUT_MS, (error) => {
      controller.abort();
      // A WASM download that crawls past the deadline never fails the load, so this is the only
      // trace of it.
      reportOnce('timeout', error);
    }).catch((error) => {
      // An instance that hung, crashed or never loaded fails every later call too, so the next
      // probe starts a fresh one.
      instance = undefined;
      throw error;
    });
  });
  queue = turn.catch(() => undefined);
  return withTimeout(turn, PROBE_TIMEOUT_MS).catch((error) => {
    abandoned = true;
    throw error;
  });
}

async function analyze(file: Blob, signal: AbortSignal): Promise<ProbedMedia> {
  const mediaInfo = await getInstance();
  try {
    const result = await mediaInfo.analyzeData(file.size, async (size, offset) => {
      // mediainfo.js has no abort; a throw here rejects `analyzeData` and ends its read loop, so a
      // timed-out analysis stops reading the file and releases its module.
      signal.throwIfAborted();
      return new Uint8Array(await file.slice(offset, offset + size).arrayBuffer());
    });
    return toProbedMedia(result);
  } catch (error) {
    // An aborted analysis is a timeout, already reported as one.
    if (!signal.aborted) reportOnce('analyze', error);
    throw error;
  }
}

function getInstance(): Promise<MediaInfo<'object'>> {
  instance ??= Promise.all([import('mediainfo.js'), import('mediainfo.js/MediaInfoModule.wasm')])
    .then(([{ default: mediaInfoFactory }, { default: wasmUrl }]) =>
      mediaInfoFactory({ format: 'object', locateFile: () => wasmUrl }),
    )
    .catch((error) => {
      // A failed chunk or WASM fetch (e.g. offline) shouldn't disable probing for the session.
      instance = undefined;
      // If this breaks for everyone (a CDN or CSP change, a mediainfo.js upgrade), estimates
      // quietly fall back to "Unknown" with no other trace.
      reportOnce('load', error);
      throw error;
    });
  return instance;
}

// Once per page and kind: every queued file retries, so an offline or stalled batch would
// otherwise report each one.
function reportOnce(kind: ProbeFailure, error: unknown) {
  if (reportedFailures.has(kind)) return;
  reportedFailures.add(kind);
  Sentry.captureException(toError(error), { tags: { component: 'media-probe', failure: kind } });
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  onTimeout?: (error: Error) => void,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(`MediaInfo probe timed out after ${ms} ms`);
      onTimeout?.(error);
      reject(error);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function toProbedMedia(result: MediaInfoResult): ProbedMedia {
  const tracks = result.media?.track ?? [];
  const general = tracks.find((t): t is GeneralTrack => t['@type'] === 'General');
  const video = tracks.find((t): t is VideoTrack => t['@type'] === 'Video');

  const probed: ProbedMedia = {};
  if (general?.Duration && general.Duration > 0) probed.durationSeconds = general.Duration;
  if (video?.Width && video.Height) {
    const quarterTurn = Math.abs(parseFloat(video.Rotation ?? '0')) % 180 === 90;
    probed.width = quarterTurn ? video.Height : video.Width;
    probed.height = quarterTurn ? video.Width : video.Height;
  }
  return probed;
}
